import React, { useState, useRef, PropsWithChildren } from 'react';
import theme from './BuyerMassInvite.module.scss';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import BuyerMassInviteUpload from './BuyerMassInviteUpload';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import BuyerMassInviteTable from './BuyerMassInviteTable';
import { Button } from '@/components/Buttons';
import { BuyerMassImportRow, validateRow, BuyerImportRowSendStatus } from '.';
import { ParsedBuyerImportRow, ParsedBuyerImportRowStatus } from '@/services/projects';
import { getUniqueId } from '@/utils/html';
import { inviteResidentialUnitBuyer, InviteBuyerOptions } from '@/services/residential-units';
import { useGetActiveProject, useInterval } from '@/utils/hooks';
import { PageTitle, SimplePageTitle } from '@/components/layout/PageTitle';
import { SimpleCard } from '@/components/Cards';
import { Breadcrumbs, isSalutatable } from '@ygtechnologies/bygr-frontend-component-library';
import { arrayGroupBy } from '@/utils/arrays';
import Banner from '@/components/Banner';
import { YesNoDialog } from '@/components/dialogs';
import { BreadcrumbsProjectLink } from '@/components/navigation';

const Wrapper: React.FC<PropsWithChildren<{}>> = ({ children }) => {
    return (
        <div className={theme.container}>
            <SimplePageTitle title={t`Invite buyers`} />

            <Breadcrumbs>
                <BreadcrumbsProjectLink to="/residential-units">
                    <Trans>All units</Trans>
                </BreadcrumbsProjectLink>

                <Breadcrumbs.Item>
                    <Trans>Invite buyers</Trans>
                </Breadcrumbs.Item>
            </Breadcrumbs>

            {children}
        </div>
    );
};

// async function sleep(time = 1000) {
//     return new Promise((resolve, reject) => {
//         setTimeout(() => {
//             resolve();
//         }, time);
//     });
// }

interface QueueItem {
    residentialUnitId: number;
    rows: BuyerMassImportRow[];
    shouldInvite: boolean;
}

interface BuyerMassInviteProps {}
const MaxActiveJobs = 2;

const BuyerMassInvite: React.FC<BuyerMassInviteProps> = (props) => {
    const [data, setData] = useState<BuyerMassImportRow[] | null>(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const jobQueueRef = useRef<QueueItem[]>([]);
    const projectId = useSelector((state: IState) => state.global.activeProject!.id);
    const project = useGetActiveProject();

    useInterval(() => {
        processQueue();
    }, 50);

    const addToQueue = (item: QueueItem, shouldInvite: boolean = true) => {
        const existingItem = jobQueueRef.current.find((x) => x.residentialUnitId === item.residentialUnitId);

        if (existingItem) {
            const existingRowIds = existingItem.rows.map((x) => x.rowId);
            const rowsToAdd = item.rows.filter((x) => !existingRowIds.includes(x.rowId));

            existingItem.rows = [...existingItem.rows, ...rowsToAdd];
        } else {
            jobQueueRef.current.push({ ...item, shouldInvite });
        }

        updateStatus(
            item.rows.map((x) => x.rowId),
            BuyerImportRowSendStatus.Queued,
            shouldInvite
        );
    };

    const processQueue = async () => {
        if (jobQueueRef.current.length === 0) {
            return;
        }

        const activeJobs = data!.filter((x) => x.sendStatus === BuyerImportRowSendStatus.Sending).length;
        if (activeJobs >= MaxActiveJobs) {
            return;
        }

        const job = jobQueueRef.current.shift()!;

        var rowsToProcess = job.rows;
        var rowIds = rowsToProcess.map((x) => x.rowId);

        try {
            const invitations = rowsToProcess.map<InviteBuyerOptions>((item) => ({
                inviteeFirstName: item.firstName,
                inviteeLastName: item.lastName,
                inviteeEmail: item.email,
                inviteePhoneNumber: item.phoneNumber,
                sendSms: item.inviteSms,
                inviteeSalutation: isSalutatable(project?.countryCode) ? item.salutation : null,
                inviteeTitle: isSalutatable(project?.countryCode) ? item.title : null,
            }));

            updateStatus(rowIds, BuyerImportRowSendStatus.Sending, job.shouldInvite);
            await inviteResidentialUnitBuyer(job.residentialUnitId, invitations, job.shouldInvite);
            updateStatus(rowIds, BuyerImportRowSendStatus.Sent, job.shouldInvite);
        } catch {
            updateStatus(rowIds, BuyerImportRowSendStatus.Failed, job.shouldInvite);
        }
    };

    const updateStatus = (rowIds: string[], newStatus: BuyerImportRowSendStatus, shouldInvite: boolean = true) => {
        setData((data) => {
            return data!.map((item) => {
                if (rowIds.includes(item.rowId)) {
                    return { ...item, sendStatus: newStatus, shouldInvite };
                }

                return item;
            });
        });
    };

    // const convertStatus = (parsedStatus: ParsedBuyerImportRowStatus) => {
    //     switch (parsedStatus) {
    //         case ParsedBuyerImportRowStatus.AlreadyHasBuyer:
    //             return BuyerImportRowStatus.AlreadyExists;
    //         case ParsedBuyerImportRowStatus.InvalidUnit:
    //             return BuyerImportRowStatus.InvalidUnit;
    //         default:
    //             return BuyerImportRowStatus.Ready;
    //     }
    // };

    // const validateRow = function(row: BuyerMassImportRow) {
    //     const errors: string[] = [];

    //     if (!validateEmail(row.email)) {
    //         errors.push('Email');
    //     }
    // };

    const hasPhoneErrors =
        !!data &&
        data.some(
            (row) =>
                row.validation.emailValid &&
                row.inviteSms &&
                !row.validation.phoneValid &&
                row.sendStatus === BuyerImportRowSendStatus.Ready
        );

    const getSendableRows = () => {
        return data!.filter(
            (row) =>
                row.sendStatus === BuyerImportRowSendStatus.Ready &&
                row.status === ParsedBuyerImportRowStatus.Ok &&
                row.validation.emailValid &&
                (row.validation.phoneValid || !row.inviteSms)
        );
    };

    const handleDataReceive = (data: ParsedBuyerImportRow[]) => {
        var transformedData = data.map<BuyerMassImportRow>((row) => ({
            ...row,
            rowId: getUniqueId(),
            sendStatus: BuyerImportRowSendStatus.Ready,
            validation: validateRow(row),
            shouldInvite: true,
        }));

        setData(transformedData);
    };

    const inviteAll = () => {
        if (!data) {
            return;
        }

        const map = arrayGroupBy(
            getSendableRows(),
            (x) => x.residentialUnitId,
            (x) => x
        );

        const queueItems: QueueItem[] = Array.from(map.entries()).map(([key, value]) => ({
            residentialUnitId: key,
            rows: value,
            shouldInvite: true,
        }));

        queueItems.forEach((item) => addToQueue(item));
    };

    const handleRetryClick = (row: BuyerMassImportRow) => {
        addToQueue({
            residentialUnitId: row.residentialUnitId,
            rows: [row],
            shouldInvite: true,
        });
    };

    const handleChange = (newData: BuyerMassImportRow[]) => {
        newData.forEach((row) => {
            row.validation = validateRow(row);
        });

        setData(newData);
    };

    if (!data) {
        return (
            <Wrapper>
                <SimpleCard className={theme.uploadCard}>
                    <BuyerMassInviteUpload projectId={projectId} onDataReceived={handleDataReceive} />
                </SimpleCard>
            </Wrapper>
        );
    }
    const readyCount = getSendableRows().length;

    return (
        <Wrapper>
            <BuyerMassInviteTable data={data} onChange={handleChange} onRetryClick={handleRetryClick} />

            {hasPhoneErrors && (
                <Banner type="warning">
                    <Trans>
                        <strong>Warning</strong> some of the telephone numbers you have imported are not valid. Please enter
                        a valid phone number or disable 'Invitation via SMS' for this buyers.
                    </Trans>
                </Banner>
            )}

            <div className={theme.buttonBar}>
                <Button color="tertiary" onClick={(e) => setData(null)}>
                    <Trans>Back</Trans>
                </Button>

                {readyCount > 0 && (
                    <>
                        <Button
                            color="secondary"
                            onClick={() => {
                                const map = arrayGroupBy(
                                    getSendableRows(),
                                    (x) => x.residentialUnitId,
                                    (x) => x
                                );

                                const queueItems: QueueItem[] = Array.from(map.entries()).map(([key, value]) => ({
                                    residentialUnitId: key,
                                    rows: value,
                                    shouldInvite: false,
                                }));

                                queueItems.forEach((item) => addToQueue(item, false));
                            }}>
                            Add {readyCount} buyer{readyCount > 1 ? 's' : ''} without inviting
                        </Button>
                        <Button
                            color="danger"
                            onClick={() => setShowConfirmDialog(true)}
                            disabled={readyCount === 0 || hasPhoneErrors}>
                            <Trans>Invite {readyCount} buyers</Trans>
                        </Button>
                    </>
                )}
            </div>

            <YesNoDialog
                show={showConfirmDialog}
                title={<Trans>Caution</Trans>}
                body={<Trans>By proceeding you'll send an invite to all buyers, do you want to continue?</Trans>}
                onAccept={() => {
                    setShowConfirmDialog(false);
                    inviteAll();
                }}
                onClose={() => setShowConfirmDialog(false)}
                confirmButtonText={<Trans>Continue</Trans>}
                cancelButtonText={<Trans>Cancel</Trans>}
            />
        </Wrapper>
    );
};

export default BuyerMassInvite;
